import React, {useEffect, useRef, useState} from "react";
import {InputTextarea} from "primereact/inputtextarea";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {InputText} from "primereact/inputtext";
import {FileUpload} from "primereact/fileupload";
import {InputSwitch} from "primereact/inputswitch";
import {Toast} from "primereact/toast";
import TextEditor from "../../components/TextEditor";
import usePost from "../../hooks/usePost";
import {webInfo} from "../../utils/webInfo";
import callToast, {catchError, showErrors} from "../../utils/helper";
import useApi from "../../hooks/useApi";
import {useParams} from "react-router-dom";
import {Badge} from "primereact/badge";
import {Skeleton} from "primereact/skeleton";

const NoticeboardCreate = () => {
    const toast = useRef<any>(null);
    const { post } = usePost();
    const {id} = useParams();
    const {get, loading} = useApi<any>();
    const updateUrl = `${webInfo.baseUrl}/api/admin/notice-board/${id}`;
    const detailsUrl = `${webInfo.baseUrl}/api/admin/notice-board/${id}`;

    const [formDataState, setFormDataState] = useState<any>({
        title: "",
        content: "",
        status: true,
        meta_title: "",
        meta_description: "",
        meta_keywords: "",
        image: null,
        file: null
    });

    useEffect(() => {
        if (!id) return
        const fetchItem = async () => {
            try {
                const res: any = await get(detailsUrl);
                const {title,image,content,file,meta_title,meta_description,meta_keywords,status} = res.data
                setFormDataState({title,image,content,file,meta_title,meta_description,meta_keywords,status});
            } catch (err) {
                console.error("Failed to fetch products", err);
            }
        };
        fetchItem();
        // eslint-disable-next-line
    },[id])

    const defaultImagePreviewer = () => {
        return (
            <div
                className="flex align-items-center cursor-pointer"
            >
                {formDataState.image ? (
                    <div className="p-fileupload-preview flex justify-content-between items-center w-full">
                        <div className="flex">
                            <img
                                alt="Default"
                                src={`${webInfo.baseUrl}/${formDataState?.image}`} height="50"/>
                            <div className="ml-2">
                                <p className="mb-2">{formDataState?.image.split("/").at(-1)}</p>
                                <div className="flex align-items-center">
                                    <p className="m-0">00.00 KB</p>
                                    <Badge className="ml-2" value="Default"></Badge>
                                </div>
                            </div>
                        </div>
                        <Button onClick={()=>setFormDataState({...formDataState,image: null})} icon="pi pi-times" className="mr-2" rounded text severity="danger" aria-label="Cancel" />
                    </div>) : (
                    <>
                        <span style={{
                            fontSize: '1em',
                            color: 'var(--text-color-secondary)'
                        }}>
                            Upload Image
                        </span>
                    </>
                )}
            </div>
        )
    }

    const onUploadPdf = (event: any) => {
        setFormDataState({
            ...formDataState,
            file: event.files[0] || null
        });
    };

    const onRemovePdf = () => {
        setFormDataState({
            ...formDataState,
            file: null
        });
    };

    const onUpload = (event: any) => {
        setFormDataState({
            ...formDataState,
            image: event.files[0] || null
        });
    };

    const onRemove = () => {
        setFormDataState({
            ...formDataState,
            image: null
        });
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormDataState({
            ...formDataState,
            [name]: value
        });
    };

    const handleStatusChange = (value: boolean) => {
        setFormDataState({
            ...formDataState,
            status: value
        });
    };

    const validateForm = () => {
        const errors: any = {};

        if (!formDataState.title || formDataState.title.trim().length === 0) {
            errors.title = ["Title is required"];
        }

        if (!formDataState.content || formDataState.content.trim().length === 0) {
            errors.content = ["Content is required"];
        }

        if (!formDataState.image) {
            errors.image = ["Featured image is required"];
        }

        if (
            formDataState.image &&
            formDataState.image.size > 1024 * 1024
        ) {
            errors.image = ["Featured image must be less than 1 MB"];
        }

        if (
            formDataState.file &&
            formDataState.file.size > 10 * 1024 * 1024
        ) {
            errors.file = ["Document must be less than 10 MB"];
        }

        if (Object.keys(errors).length > 0) {
            showErrors(errors, toast);
            return false;
        }

        return true;
    };

    const updateNotice = async () => {
        if (!validateForm()) return;

        const formData = new FormData();

        const payload: any = {
            ...formDataState
        };

        for (const key in payload) {
            const value = payload[key as keyof typeof payload];

            if (value === null || value === undefined) continue;

            if (key === "status") {
                formData.append("status", formDataState?.status ? "1" : "0");
                continue;
            }

            if (typeof value === "number" || typeof value === "boolean") {
                formData.append(key, String(value));
                continue;
            }

            if (typeof value === "string" && value.trim().length === 0) continue;

            if (key === "image" && value instanceof File) {
                formData.append("image", value);
                continue;
            }

            if (key === "file" && value instanceof File) {
                formData.append("file", value);
                continue;
            }

            if (key === "image" && typeof value === "string") continue;
            if (key === "file" && typeof value === "string") continue;

            formData.append(key, String(value));
        }

        try {
            // @ts-ignore
            const res: any = await post(updateUrl, false, formData);

            if (res.success) {
                callToast(toast, true, res.data.message || "Notice updated successfully");

                setFormDataState({
                    title: "",
                    content: "",
                    status: true,
                    meta_title: "",
                    meta_description: "",
                    meta_keywords: "",
                    image: null,
                    file: null
                });
            } else {
                showErrors(res.error?.errors || res.error, toast);
            }
        } catch (e) {
            catchError(e);
        }
    };

    const header = (
        <div className="flex align-items-center justify-content-between p-4 pb-0">
            <h4 className="m-0">Notice Details</h4>
            <div className="flex align-items-center">
                <label htmlFor="">Publish Now</label>
                <InputSwitch
                    className="ml-2"
                    checked={Number(formDataState.status)===1}
                    onChange={(e) => handleStatusChange(e.value)}
                />
            </div>
        </div>
    );

    const defaultPdfPreviewer = () => {
        return (
            <div
                className="flex align-items-center cursor-pointer"
            >
                {formDataState.file ? (
                    <div className="p-fileupload-preview flex justify-content-between items-center w-full">
                        <div className="flex">
                            <img
                                alt="Default"
                                src={`/pdf.jpg`} height="50"/>
                            <div className="ml-2">
                                <p className="mb-2">{formDataState?.file?.split("/").at(-1)}</p>
                                <div className="flex align-items-center">
                                    <p className="m-0">00.00 KB</p>
                                    <Badge className="ml-2" value="Default"></Badge>
                                </div>
                            </div>
                        </div>
                        <Button onClick={()=>setFormDataState({...formDataState,file: null})} icon="pi pi-times" className="mr-2" rounded text severity="danger" aria-label="Cancel" />
                    </div>) : (
                    <>
                        <span style={{
                            fontSize: '1em',
                            color: 'var(--text-color-secondary)'
                        }}>
                            Upload Image
                        </span>
                    </>
                )}
            </div>
        )
    }

    const content = (
        <div>
            <div className="grid">
                <div className="col-12 md:col-7">
                    <div className="pr-2">
                        <div className="my-2">
                            <label className="font-medium">Title *</label>
                            <InputText
                                name="title"
                                value={formDataState.title}
                                className="w-full"
                                onChange={handleChange}
                                placeholder="Title"
                            />
                        </div>

                        <div className="col-12 px-0 mt-3">
                            <label className="font-medium">
                                Thumbnail Image (1024 kB max) *
                            </label>
                            <FileUpload
                                name="file"
                                url="/api/upload"
                                multiple={false}
                                accept="image/*"
                                maxFileSize={1024 * 1024}
                                emptyTemplate={defaultImagePreviewer}
                                onSelect={onUpload}
                                onRemove={onRemove}
                            />
                        </div>

                        <div className="col-12 px-0 mt-3">
                            <label className="font-medium">Document (10 mb max)</label>
                            <FileUpload
                                name="file"
                                url="/api/upload"
                                multiple={false}
                                accept="application/pdf"
                                maxFileSize={10 * 1024 * 1024}
                                emptyTemplate={defaultPdfPreviewer}
                                onSelect={onUploadPdf}
                                onRemove={onRemovePdf}
                            />
                        </div>

                        <div className="mt-3">
                            <label className="font-medium">Details *</label>
                            <TextEditor
                                value={formDataState.content || ""}
                                setValue={(content: string) =>
                                    setFormDataState((prev: any) => ({
                                        ...prev,
                                        content
                                    }))
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-5">
                    <div className="grid p-4">
                        <div className="col-12">
                            <h4 className="mb-3">SEO Information</h4>
                        </div>

                        <div className="col-12">
                            <label className="font-medium">Meta Title</label>
                            <InputText
                                name="meta_title"
                                placeholder="Meta Title"
                                className="w-full"
                                value={formDataState.meta_title}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-12">
                            <label className="font-medium">Meta Keywords</label>
                            <InputText
                                name="meta_keywords"
                                placeholder="keyword1, keyword2, keyword3"
                                className="w-full"
                                value={formDataState.meta_keywords || ""}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-12 mt-3">
                            <label className="font-medium">Meta Description</label>
                            <InputTextarea
                                name="meta_description"
                                rows={4}
                                placeholder="Meta Description"
                                className="w-full"
                                value={formDataState.meta_description}
                                onChange={handleChange}
                                autoResize
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 flex justify-content-end gap-2 mt-3">
                <Button
                    label="Add Notice"
                    icon="pi pi-check"
                    onClick={updateNotice}
                    type="button"
                />
            </div>
        </div>
    )

    const loader = <div className="grid">
        <div className="col-12 md:col-7">
            <div className="pr-2"><Skeleton className="w-full" height="60rem"></Skeleton></div>
        </div>
        <div className="col-12 md:col-5">
            <div className="pl-2"><Skeleton className="w-full" height="60rem"></Skeleton></div>
        </div>
    </div>

    return (
        <Card header={header}>
            <Toast ref={toast} />
            {loading ? loader : content}
        </Card>
    );
};

export default NoticeboardCreate;